/**
 * One-shot capture of how opaque the edited splat is on screen: a temporary
 * camera that follows the editor camera renders only the edit layer into a
 * transparent target, and the alpha channel is read back. SuperSplat's flood
 * fill works on this image (its "render.offscreen").
 */
import * as pc from 'playcanvas';

export interface AlphaImage {
    width: number;
    height: number;
    /** width*height opacities (0-255), row-major, top row first. */
    alpha: Uint8Array;
}

export async function captureLayerAlpha(
    app: pc.AppBase, camera: pc.CameraComponent, layer: pc.Layer, requestRender: () => void,
): Promise<AlphaImage> {
    const device = app.graphicsDevice;
    const width = Math.max(1, Math.floor(device.width));
    const height = Math.max(1, Math.floor(device.height));
    const texture = new pc.Texture(device, {
        name: 'splatEditOpacity', width, height,
        format: pc.PIXELFORMAT_RGBA8, mipmaps: false,
        minFilter: pc.FILTER_NEAREST, magFilter: pc.FILTER_NEAREST,
        addressU: pc.ADDRESS_CLAMP_TO_EDGE, addressV: pc.ADDRESS_CLAMP_TO_EDGE,
    });
    const target = new pc.RenderTarget({ colorBuffer: texture, depth: false });
    const entity = new pc.Entity('splat-edit-opacity-camera');
    entity.addComponent('camera', {
        layers: [layer.id],
        clearColor: new pc.Color(0, 0, 0, 0),
        renderTarget: target,
        priority: (camera.priority ?? 0) - 1,
        projection: camera.projection,
        fov: camera.fov,
        orthoHeight: camera.orthoHeight,
        nearClip: camera.nearClip,
        farClip: camera.farClip,
        horizontalFov: camera.horizontalFov,
    } as never);
    camera.entity.addChild(entity);
    try {
        await new Promise<void>((resolve) => {
            app.once('postrender', () => resolve());
            requestRender();
        });
        const pixels = await texture.read(0, 0, width, height, { renderTarget: target, immediate: true } as never) as Uint8Array;
        // WebGL reads bottom-up
        const flip = !!(device as unknown as { isWebGL2?: boolean }).isWebGL2;
        const alpha = new Uint8Array(width * height);
        for (let row = 0; row < height; row++) {
            const src = (flip ? height - 1 - row : row) * width;
            for (let col = 0; col < width; col++) alpha[row * width + col] = pixels[(src + col) * 4 + 3];
        }
        return { width, height, alpha };
    } finally {
        entity.destroy();
        target.destroy();
        texture.destroy();
    }
}
